import { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  MenuItem,
  Paper,
  Stack,
  TextField,
} from '@mui/material';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import { PageHeader } from '../components/PageHeader';
import {
  useGeneratePayrollExport,
  usePayrollExports,
  usePayrollPreview,
} from '../hooks/useOps';
import type { PayrollExport, PayrollProvider } from '../api/types';
import { formatDate, humanizeStatus, statusColor } from '../lib/format';

function currentPeriod(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function PayrollPage() {
  const { data: exports = [], isLoading, error } = usePayrollExports();
  const { data: preview } = usePayrollPreview();
  const generate = useGeneratePayrollExport();

  const [period, setPeriod] = useState(currentPeriod());
  const [provider, setProvider] = useState<PayrollProvider>('talenta');
  const [genError, setGenError] = useState<string | null>(null);

  const blocked = (preview?.missing.length ?? 0) > 0;

  const onGenerate = async () => {
    setGenError(null);
    try {
      await generate.mutateAsync({ period, provider });
    } catch (e) {
      setGenError((e as Error).message);
    }
  };

  const columns: GridColDef<PayrollExport>[] = [
    { field: 'period', headerName: 'Period', width: 110 },
    { field: 'provider', headerName: 'Provider', width: 120, renderCell: (p) => <Chip size="small" variant="outlined" label={p.value} /> },
    { field: 'personCount', headerName: 'People', width: 90 },
    {
      field: 'status',
      headerName: 'Status',
      width: 130,
      renderCell: (p) => <Chip size="small" variant="outlined" color={statusColor(p.value)} label={humanizeStatus(p.value)} />,
    },
    { field: 'generatedAt', headerName: 'Generated', width: 150, valueGetter: (v) => (v ? formatDate(v as string) : '—') },
    {
      field: 'payloadUrl',
      headerName: 'File',
      minWidth: 220,
      flex: 1,
      renderCell: (p) =>
        p.value ? (
          <Box component="a" href={`/${p.value}`} target="_blank" rel="noreferrer" sx={{ fontSize: 13 }}>
            {String(p.value).split('/').pop()}
          </Box>
        ) : (
          '—'
        ),
    },
  ];

  return (
    <Box>
      <PageHeader title="Payroll export" />

      <Alert severity="info" sx={{ mb: 2 }}>
        <AlertTitle>Roster hand-off only</AlertTitle>
        Ops exports <strong>who / when</strong> — the structured roster. Salary, BPJS, and PPh 21 are
        computed by the payroll provider. Nothing here calculates statutory deductions.
      </Alert>

      {blocked && preview && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          <AlertTitle>Missing data — export blocked</AlertTitle>
          {preview.missing.map((m) => (
            <div key={m.personId}>
              <strong>{m.personName}</strong>: {m.missing.join(', ')}
            </div>
          ))}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
        {genError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {genError}
          </Alert>
        )}
        <Stack direction={{ xs: 'column', sm: 'row' }} gap={2} alignItems={{ sm: 'center' }}>
          <TextField
            label="Period"
            type="month"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            select
            label="Provider"
            value={provider}
            onChange={(e) => setProvider(e.target.value as PayrollProvider)}
            sx={{ minWidth: 160 }}
          >
            <MenuItem value="talenta">Talenta</MenuItem>
            <MenuItem value="manual">Manual</MenuItem>
          </TextField>
          <Box flexGrow={1} />
          <Button
            variant="contained"
            disabled={generate.isPending || blocked || !period}
            onClick={onGenerate}
          >
            Generate export ({preview?.personCount ?? 0} people)
          </Button>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {(error as Error).message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ height: 460 }}>
        <DataGrid
          rows={exports}
          columns={columns}
          getRowId={(r) => r.id}
          loading={isLoading}
          disableRowSelectionOnClick
          pageSizeOptions={[10, 25, 50]}
          initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        />
      </Paper>
    </Box>
  );
}
